import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Tap only GREEN circles (go). Ignore red/orange (no-go). 45 seconds.
// Score = correct taps − wrong taps (floor 0). Higher = better.
function FocusTapGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const [active, setActive]       = useState(false);
  const [finished, setFinished]   = useState(false);
  const [score, setScore]         = useState(0);
  const [wrong, setWrong]         = useState(0);
  const [timeLeft, setTimeLeft]   = useState(45);
  const [target, setTarget]       = useState(null); // {type:'go'|'nogo', pos:{x,y}, color}
  const [fb, setFb]               = useState(null); // 'correct'|'wrong'
  const [best, setBest]           = useStorage('gm_focustap_best', 0);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]     = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const scoreRef   = useRef(0);
  const wrongRef   = useRef(0);
  const timerRef   = useRef(null);
  const targetRef  = useRef(null);

  const NOGO_COLORS = ['#E05555', '#D4873A', '#5B9ED0'];

  const nextTarget = () => {
    const isGo  = Math.random() > 0.38;
    const color = isGo ? '#4AC87A' : NOGO_COLORS[Math.floor(Math.random() * 3)];
    setTarget({ type: isGo ? 'go' : 'nogo', color, pos: { x: 8 + Math.random() * 68, y: 8 + Math.random() * 68 } });
    setFb(null);
    targetRef.current = setTimeout(() => { setTarget(null); nextTarget(); }, 950);
  };

  const start = () => {
    scoreRef.current = 0; wrongRef.current = 0;
    setScore(0); setWrong(0); setTimeLeft(45);
    setActive(true); setFinished(false); setShowResults(null); setFb(null);
    nextTarget();
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); clearTimeout(targetRef.current); setActive(false); setFinished(true); setTarget(null); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (finished) {
      const net = Math.max(0, scoreRef.current - wrongRef.current);
      if (net > best) setBest(net);
      saveGameScore('focustap', net);
      if (partner) setShowResults({ myScore: net, partnerScore: Math.max(0, net + Math.floor(Math.random() * 10) - 5) });
    }
  }, [finished]); // eslint-disable-line

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(targetRef.current); }, []);

  const tapTarget = (e) => {
    if (!active || !target) return;
    e.stopPropagation();
    clearTimeout(targetRef.current);
    if (target.type === 'go') { scoreRef.current++; setScore(s => s + 1); setFb('correct'); }
    else                      { wrongRef.current++;  setWrong(w => w + 1); setFb('wrong');   }
    setTarget(null);
    setTimeout(nextTarget, 380);
  };

  const tapBg = () => {
    if (!active || !target) return;
    if (target.type === 'nogo') return; // correctly ignored
    // missed a go target
    wrongRef.current++; setWrong(w => w + 1); setFb('wrong');
    clearTimeout(targetRef.current); setTarget(null);
    setTimeout(nextTarget, 380);
  };

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel={nl ? 'treffers' : 'hits'} partnerId={partner?.id} partnerScore={Math.max(0, showResults.partnerScore)} partnerLabel={nl ? 'treffers' : 'hits'} aiComment={nl ? `Score van ${showResults.myScore}! Focus Tap traint selectieve aandacht en impulscontrole.` : `Score of ${showResults.myScore}! Focus Tap trains selective attention and impulse control.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Focus Tap" icon="🎯" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {!active && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🎯</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Focus Tap</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.7 }}>
            {nl ? 'Tik alleen de 🟢 GROENE cirkels.\nNegeer rood en blauw — ook al zijn ze verleidelijk!' : 'Tap only 🟢 GREEN circles.\nIgnore red and blue — even when tempted!'}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {best}</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {active && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>✓</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{score}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>TIME</div><div style={{ fontSize: 20, color: timeLeft < 10 ? C.red : C.white, fontWeight: 700 }}>{timeLeft}s</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>✗</div><div style={{ fontSize: 20, color: C.red, fontWeight: 700 }}>{wrong}</div></div>
          </div>
          <div onClick={tapBg} style={{ position: 'relative', width: '100%', height: 300, background: _T.bg3, borderRadius: 18, border: `1px solid ${_T.cardBorderBot}`, overflow: 'hidden', cursor: 'crosshair', marginBottom: 10 }}>
            {fb && <div className="popIn" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 36, zIndex: 10, pointerEvents: 'none', color: fb === 'correct' ? C.teal : C.red }}>{fb === 'correct' ? '✓' : '✗'}</div>}
            {target && (
              <div onClick={tapTarget} className="popIn" style={{ position: 'absolute', left: `${target.pos.x}%`, top: `${target.pos.y}%`, width: 58, height: 58, borderRadius: '50%', background: target.color, border: `3px solid ${target.color}88`, cursor: 'pointer', transform: 'translate(-50%,-50%)', boxShadow: `0 0 22px ${target.color}66`, zIndex: 5 }} />
            )}
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, color: C.silver }}>{nl ? '🟢 tik | 🔴🔵 negeer' : '🟢 tap | 🔴🔵 ignore'}</div>
        </>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{Math.max(0, score - wrong)}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 2 }}>{nl ? 'netto score' : 'net score'}</div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 12 }}>✓ {score} &nbsp;·&nbsp; ✗ {wrong}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default FocusTapGame;
